/**
 * Finance › Overview (FINANCE.md §8.2): the questions the dashboard must answer quickly, one row
 * per entity and a total — how much cash, what came in and went out last month, NOI, free cash
 * flow, against budget, where cash will be, and what needs attention — every figure from the
 * same code the other finance screens use, and the Finance Agent's latest words beside them.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type FinanceOverviewResponse } from '../../api.js';
import { formatMoney } from '../../money.js';

function Signed({ cents }: { cents: string }): ReactNode {
  const negative = cents.startsWith('-');
  return (
    <span className={negative ? 'chip chip-attention' : cents === '0' ? '' : 'chip chip-ok'}>
      {formatMoney(cents)}
    </span>
  );
}

export function OverviewPage(): ReactNode {
  const overview = useQuery({
    queryKey: ['finance', 'overview'],
    queryFn: () => api.get<FinanceOverviewResponse>('/api/finance/overview'),
  });
  const data = overview.data;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Finance</h1>
          <p className="subtitle">
            How much cash, what came in and went out last month, NOI, free cash flow, against
            budget, where cash will be, and what needs attention — per entity. Code computes every
            figure; the Finance Agent&apos;s words are labelled.
          </p>
        </div>
      </header>

      {overview.isLoading && <p className="muted">Loading…</p>}
      {overview.isError && (
        <p className="error">Could not load the overview: {overview.error.message}</p>
      )}

      {data && data.entities.length === 0 && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {data && data.entities.length > 0 && (
        <>
          <section className="card">
            <h2>The portfolio, {data.month}</h2>
            <div className="stat-row">
              <div className="stat">
                <span className="stat-label">Cash today</span>
                <strong>{formatMoney(data.totals.net_cash_cents)}</strong>
              </div>
              <div className="stat">
                <span className="stat-label">Came in</span>
                <strong>{formatMoney(data.totals.revenue_cents)}</strong>
              </div>
              <div className="stat">
                <span className="stat-label">Went out</span>
                <strong>{formatMoney(data.totals.expenses_cents)}</strong>
              </div>
              <div className="stat">
                <span className="stat-label">NOI</span>
                <strong>{formatMoney(data.totals.noi_cents)}</strong>
              </div>
              <div className="stat">
                <span className="stat-label">Free cash flow</span>
                <strong>{formatMoney(data.totals.free_cash_flow_cents)}</strong>
              </div>
              <div className="stat">
                <span className="stat-label">Cash in 90 days, base</span>
                <strong>{formatMoney(data.totals.base_90_cents)}</strong>
              </div>
              <div className="stat">
                <span className="stat-label">Needs attention</span>
                <strong>{data.totals.attention}</strong>
              </div>
            </div>
            <p className="muted small">
              Last completed month as of {data.as_of}. Cash is the ledger&apos;s word as QuickBooks
              last reported it; the 90-day figure is the forecast&apos;s base case summed across
              entities. {data.calc_method}
            </p>
          </section>

          {data.narration && (
            <section className="card narration">
              <h2>The Finance Agent, this morning</h2>
              <p>{data.narration.summary}</p>
              <p className="muted small">
                AI narration · run {data.narration.run_id.slice(0, 8)} ·{' '}
                {new Date(data.narration.generated_at).toLocaleString()} · the items themselves are
                on <Link to="/finance/monthly-review">Monthly Review</Link> and{' '}
                <Link to="/finance/budgets">Budgets</Link>.
              </p>
            </section>
          )}

          <section className="card">
            <h2>By entity</h2>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Entity</th>
                    <th className="num">Cash</th>
                    <th className="num">In 30 days</th>
                    <th className="num">In 90 days</th>
                    <th>Floor</th>
                    <th className="num">Came in</th>
                    <th className="num">Went out</th>
                    <th className="num">NOI</th>
                    <th className="num">vs budget</th>
                    <th className="num">Free cash flow</th>
                    <th>Month</th>
                    <th className="num">Attention</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entities.map((e) => (
                    <tr key={e.entity_id} className={e.attention > 0 ? '' : 'muted'}>
                      <th scope="row">
                        <Link to={`/finance/cash?entity=${e.entity_id}`}>{e.entity_name}</Link>
                        {e.unknown_balances > 0 && (
                          <span className="muted small">
                            {' '}
                            · {e.unknown_balances} balance unknown
                          </span>
                        )}
                      </th>
                      <td className="num">{formatMoney(e.net_cash_cents)}</td>
                      <td className="num">{formatMoney(e.base_30_cents)}</td>
                      <td className="num">{formatMoney(e.base_90_cents)}</td>
                      <td>
                        {e.floor_cents === null ? (
                          <span className="muted">none</span>
                        ) : e.floor_breached ? (
                          <span className="status status-conflict">
                            under {formatMoney(e.floor_cents)}
                          </span>
                        ) : (
                          <span className="status status-approved">
                            holds {formatMoney(e.floor_cents)}
                          </span>
                        )}
                      </td>
                      <td className="num">{formatMoney(e.revenue_cents)}</td>
                      <td className="num">{formatMoney(e.expenses_cents)}</td>
                      <td className="num">
                        <strong>{formatMoney(e.noi_cents)}</strong>
                      </td>
                      <td className="num">
                        {e.noi_variance_cents === null ? (
                          <span className="muted">no budget</span>
                        ) : (
                          <Signed cents={e.noi_variance_cents} />
                        )}
                      </td>
                      <td className="num">{formatMoney(e.free_cash_flow_cents)}</td>
                      <td>
                        {e.month_ready === null ? (
                          <span className="muted">no lines</span>
                        ) : e.month_ready ? (
                          <span className="status status-approved">ready</span>
                        ) : (
                          <Link to={`/finance/monthly-review?entity=${e.entity_id}`}>
                            {e.open_items} open
                          </Link>
                        )}
                        {e.pending_lines > 0 && (
                          <span className="muted small"> · {e.pending_lines} pending</span>
                        )}
                      </td>
                      <td className="num" title={e.narration_high_items.join(' · ')}>
                        {e.attention}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted small">
              Attention = open review items + a breached floor + the Finance Agent&apos;s
              high-severity items for the entity (hover the number). &ldquo;vs budget&rdquo; is the
              month&apos;s NOI against the entity-level budget, positive favourable.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
